/* eslint-disable no-negated-condition */
/* eslint-disable react-hooks/exhaustive-deps */
const React = require("react");

const { useRef, useEffect, useState, useCallback, useMemo } = React;

const PropTypes = require("prop-types");

const RestClient = require("frontend-restclient");

const useModuleEvent = require("./useModuleEvent");
const {
  MODULES_CONFIGURATION_PATH,
  RESOLVE_PROPS_PATH,
  LOADED_MANIFESTS_KEY,
  MANIFEST_LOADING_KEY,
  REMOTE_MODULE_KEY,
  SCRIPT_ERROR_EVENT_KEY,
} = require("../../config/constants");
const { validateSchema } = require("../utils/manifest");

const useManifest = ({
  consumer,
  host,
  path,
  root,
  name,
  cache,
  restClientConfig,
  preloadModule = false,
}) => {
  const enabled = useRef(typeof document !== "undefined");
  const renderMethod = useRef();

  const restClient = RestClient({
    timeout: 15000,
    cache: {
      maxSize: 0,
      maxAge: 0,
    },
    ...restClientConfig,
  });

  const [appData, setAppData] = useState();
  const [error, setError] = useState();

  const url = useMemo(
    () => `${host}${path}${root}/${MODULES_CONFIGURATION_PATH}`,
    [host, path, root, name]
  );
  const resolvePropsUrl = useMemo(
    () => `${host}${path}${root}/${RESOLVE_PROPS_PATH}?module=${name}`,
    [host, path, root, name]
  );

  const { listen, dispatch, unlisten } = useModuleEvent(url);

  const getResolveProps = useCallback(async () => {
    if (preloadModule) {
      return Promise.resolve({});
    }

    try {
      const { data } = await restClient.get(resolvePropsUrl, {
        withCredentials: true,
      });

      return data;
    } catch (err) {
      throw `Error loading resolveProps: ${err}`;
    }
  }, [resolvePropsUrl]);

  const onLoadEvt = async ({ detail: { data, error: errorPayload } }) => {
    if (errorPayload) {
      setError(errorPayload);
    } else {
      try {
        const { manifest, runtimeProps, resolvedProps, resolvePropsModules } =
          data;
        const hasResolveProps =
          resolvePropsModules &&
          resolvePropsModules.indexOf(name.toLowerCase()) !== -1;

        let moduleResolvedProps = resolvedProps[name];

        if (hasResolveProps && !moduleResolvedProps) {
          try {
            moduleResolvedProps = await getResolveProps();
          } catch (e) {
            console.error(`Error loading resolveProps: ${e}`);
          }
        }

        setAppData({
          manifest,
          runtimeProps,
          resolvedProps: moduleResolvedProps,
        });
      } catch (err) {
        console.error(`onLoadEvt. Error loading: ${err}`);
        setError(err);
      }
    }
  };

  const save = useCallback(
    (data, cb) => {
      if (enabled.current) {
        try {
          window[LOADED_MANIFESTS_KEY][url] = data;

          if (cb) {
            setTimeout(() => cb && cb(), 1000); // TODO: improve race condition.
          }
        } catch (e) {
          console.error(`Error saving manifest: ${e}`);
        }
      }
    },
    [url]
  );

  const getManifest = useCallback(async () => {
    try {
      const { data } = await restClient.get(url, {
        params: {
          consumer,
        },
        withCredentials: true,
      });

      const { manifest, resolvePropsModules = [], ...runtimeProps } = data;

      return { manifest, resolvePropsModules, runtimeProps };
    } catch (err) {
      throw `Error loading manifest: ${err}`;
    }
  }, [url]);

  const onLoadedModule = useCallback(
    (data) => {
      const { resolvedProps, error: err, ...otherData } = data;

      dispatch(
        "load",
        err
          ? { error: err }
          : {
              data: {
                ...otherData,
                resolvedProps: { [name]: resolvedProps },
                resolverName: name,
              },
            }
      );
    },
    [name, dispatch]
  );

  const download = useCallback(async () => {
    save(MANIFEST_LOADING_KEY);

    try {
      const manifestData = await getManifest();

      const resolveProps =
        manifestData.resolvePropsModules.indexOf(name.toLowerCase()) !== -1;

      const data = { ...manifestData };

      if (resolveProps) {
        data.resolvedProps = await getResolveProps();
      }

      validateSchema(manifestData)

      save(manifestData, () => {
        setAppData(data);
      });
    } catch (err) {
      console.error(`Error on load module: ${err}`);
      save(undefined, () => {
        setError(err);
        onLoadedModule({ error: err });
      });
    }
  }, [url]);

  const getComponentMethod = useCallback(() => {
    if (renderMethod.current) {
      return window[renderMethod.current];
    }

    return null;
  }, [renderMethod.current]);

  const deleteComponentMethod = useCallback(() => {
    if (renderMethod.current) {
      delete window[renderMethod.current];
    }

    return null;
  }, [renderMethod.current]);

  const loadedManifest = useCallback(async (data) => {
    try {
      const { manifest, runtimeProps, resolvePropsModules } = data;
      const resolveProps =
        resolvePropsModules.indexOf(name.toLowerCase()) !== -1;

      const componentData = { manifest, runtimeProps };

      if (!resolveProps) {
        setAppData(componentData);
      } else {
        setAppData({
          ...componentData,
          resolvedProps: await getResolveProps(),
        });
      }
    } catch (err) {
      console.error(`Error on load module (load): ${err}`);
      setError(err);
    }
  }, []);

  const get = useCallback(() => {
    if (enabled.current) {
      try {
        return window[LOADED_MANIFESTS_KEY][url];
      } catch (e) {
        console.error(`Error getting manifest: ${e}`);

        return undefined;
      }
    }

    return null;
  }, [url]);

  const clearStore = useCallback(() => {
    try {
      if (enabled.current) {
        window[LOADED_MANIFESTS_KEY][url] = undefined;
      }
    } catch (e) {
      console.error(`Error clearing manifest store: ${e}`);
    }
  }, [url]);

  const scriptErrorCb = useCallback(() => {
    console.error(`Error loading script from first attempt`);
    setAppData();
  }, []);

  useEffect(() => {
    if (enabled.current) {
      if (!window[LOADED_MANIFESTS_KEY]) {
        window[LOADED_MANIFESTS_KEY] = {};
      }
    }
  }, []);

  useEffect(() => {
    let scriptErrorTimeout;

    if (!appData) {
      const savedManifest = cache && get();

      // Case 1: never loaded
      if (!savedManifest) {
        download();
      }
      // Case 2: manifest is Loading
      else if (savedManifest === MANIFEST_LOADING_KEY) {
        listen("load", onLoadEvt, { once: true });
      }
      // Case 3: Manifest loaded
      else {
        loadedManifest(savedManifest);
      }
    } else {
      renderMethod.current = `${REMOTE_MODULE_KEY}_${appData.manifest.APP_NAME}-${name}`;
      listen(SCRIPT_ERROR_EVENT_KEY, scriptErrorCb, { once: true });
      scriptErrorTimeout = setTimeout(() => {
        unlisten(SCRIPT_ERROR_EVENT_KEY, scriptErrorCb);
      }, 3000);
    }

    return () => {
      clearTimeout(scriptErrorTimeout);
      unlisten(SCRIPT_ERROR_EVENT_KEY, scriptErrorCb);
    };
  }, [url, appData]);

  return {
    appData,
    error,
    getComponentMethod,
    deleteComponentMethod,
    onLoadedModule,
    clearStore,
    dispatchEvent: dispatch,
  };
};

useManifest.propTypes = {
  host: PropTypes.string.isRequired,
  path: PropTypes.string.isRequired,
  root: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  cache: PropTypes.bool.isRequired,
  restClientConfig: PropTypes.shape(),
};

module.exports = useManifest;
